import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Document, Page, pdfjs } from "react-pdf";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useNavigate, useLocation } from "react-router-dom";
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  DocumentIcon,
  DocumentArrowUpIcon,
  BuildingOfficeIcon,
  UserIcon,
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MicrophoneIcon,
  ArrowUpTrayIcon,
  DocumentPlusIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  FolderIcon,
} from "@heroicons/react/24/outline";
import Button from "../common/Button";
import { SortState, ViewMode } from "../common/ListViewControls";
import ListPage, { ListColumn } from "../common/ListPage";
import { hasPendingSuggestions, isTemplateProcessing } from "../../utilities/formTemplates";
import { useLocalStorageState } from "../../hooks/useLocalStorageState";
import { FormTemplate } from "../../db/schema";
import { resolveFormPdfUrl } from "../../services/pdfService";
import FormSubmissionList, { FormSubmissionListHandle } from "./FormSubmissionList";
import Tooltip from "../common/Tooltip";
import ItemCard from "../common/ItemCard";
import { useAuth } from "../../context/AuthContext";
import { fillFormFromCase, fillFormFromFiles, processFormFill } from "../../services/formAudioProcessingService";
import CaseFillPickerModal from "./CaseFillPickerModal";
import FilesFillPickerModal from "./FilesFillPickerModal";
import AudioPlayback from "../Menu/AudioPlayback";
import UploadModal from "../Menu/UploadModal";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const TemplateCard: React.FC<{
  template: FormTemplate;
  onNavigate: (path: string) => void;
  onDelete: (template: FormTemplate) => void;
  highlighted?: boolean;
}> = ({ template, onNavigate, onDelete, highlighted }) => (
  <ItemCard
    onClick={() => onNavigate(hasPendingSuggestions(template) ? `/forms/${template.id}/edit` : `/forms/${template.id}`)}
    disabled={isTemplateProcessing(template)}
    className={isTemplateProcessing(template) ? "!opacity-50 grayscale select-none" : undefined}
    highlighted={highlighted}
    icon={<DocumentIcon className="h-6 w-6 text-primary" />}
    title={<span data-tour="form-template-name">{template.name}</span>}
    actions={
      template.scope !== "agency" &&
      !isTemplateProcessing(template) && (
        <span
          className="inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-base-200 text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70 transition-colors"
            onClick={() => onNavigate(`/forms/${template.id}/edit`)}
            title="Edit"
          >
            <PencilIcon className="h-3.5 w-3.5" />
          </button>
          <button
            className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-950/40 text-base-content/40 hover:text-red-500 transition-colors"
            onClick={() => onDelete(template)}
            title="Delete"
          >
            <TrashIcon className="h-3.5 w-3.5" />
          </button>
        </span>
      )
    }
    subtitle={isTemplateProcessing(template) ? undefined : template.description || undefined}
    meta={
      <>
        {isTemplateProcessing(template) && (
          <span className="text-xs text-blue-600 dark:text-blue-300 flex items-center gap-1">
            <span className="loading loading-spinner size-3" /> Processing...
          </span>
        )}
        {hasPendingSuggestions(template) && (
          <span className="text-xs text-amber-700 dark:text-amber-400">Suggestions ready</span>
        )}
        <span className="text-xs text-base-content/40">
          {template.fields?.length || 0} fields
        </span>
        <span
          className={`text-[10px] px-1.5 py-0.5 rounded-full ${
            template.is_fillable
              ? "bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-400"
              : "bg-yellow-100 text-yellow-800 dark:bg-amber-950/40 dark:text-amber-400"
          }`}
        >
          {template.is_fillable ? "Fillable" : "Flat"}
        </span>
        <span className="inline-flex items-center gap-0.5 text-[10px] text-base-content/40">
          {template.scope === "agency" ? (
            <BuildingOfficeIcon className="h-3 w-3" />
          ) : (
            <UserIcon className="h-3 w-3" />
          )}
          {template.scope === "agency" ? "Dept" : "Personal"}
        </span>
      </>
    }
  />
);

type TemplateSortKey =
  | "name"
  | "fields"
  | "type"
  | "scope"
  | "created_at"
  | "updated_at";

interface FormTemplateManagerProps {
  templates: FormTemplate[];
  loading: boolean;
  onRefresh: () => Promise<void>;
  /** When provided, show the template detail view instead of the list. */
  viewTemplate?: FormTemplate;
  /** Opens the template-upload modal (owned by FormsRouter). */
  onUploadClick?: () => void;
  /** Files dropped anywhere on the list; the page opens the upload modal with them. */
  onFilesDropped?: (files: File[]) => void;
  /** Set by the legacy /forms/upload route so old links still open the modal. */
  autoOpenUpload?: boolean;
}

const FormTemplateManager: React.FC<FormTemplateManagerProps> = ({
  templates,
  loading,
  onRefresh,
  viewTemplate,
  onUploadClick,
  onFilesDropped,
  autoOpenUpload = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string>("");
  const [hasEvidenceIntegration, setHasEvidenceIntegration] = useState(false);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useLocalStorageState<ViewMode>(
    "forms.viewMode",
    "grid"
  );
  const [sort, setSort] = useLocalStorageState<SortState<TemplateSortKey>>(
    "forms.sort",
    { key: "created_at", dir: "desc" }
  );

  const filteredTemplates = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return templates;
    return templates.filter(
      (t) =>
        t.name.toLowerCase().includes(query) ||
        (t.description || "").toLowerCase().includes(query)
    );
  }, [templates, search]);

  const sortedTemplates = useMemo(() => {
    return [...filteredTemplates].sort((a, b) => {
      let cmp = 0;
      switch (sort.key) {
        case "name":
          cmp = a.name.localeCompare(b.name);
          break;
        case "fields":
          cmp = (a.fields?.length || 0) - (b.fields?.length || 0);
          break;
        case "type":
          cmp = Number(a.is_fillable) - Number(b.is_fillable);
          break;
        case "scope":
          cmp = (a.scope || "").localeCompare(b.scope || "");
          break;
        case "updated_at":
          cmp =
            new Date(a.updated_at || 0).getTime() -
            new Date(b.updated_at || 0).getTime();
          break;
        case "created_at":
        default:
          cmp =
            new Date(a.created_at || 0).getTime() -
            new Date(b.created_at || 0).getTime();
          break;
      }
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [filteredTemplates, sort]);

  useEffect(() => {
    if (!user?.agency_id) return;
    axios
      .get("/api/settings/evidence-integration")
      .then((res) => setHasEvidenceIntegration(!!res.data.integration))
      .catch(() => setHasEvidenceIntegration(false));
  }, [user?.agency_id]);

  useEffect(() => {
    let cancelled = false;
    if (!viewTemplate?.original_pdf_url) {
      setPreviewPdfUrl("");
      return;
    }
    resolveFormPdfUrl(viewTemplate.original_pdf_url).then((u) => {
      if (!cancelled) setPreviewPdfUrl(u);
    });
    return () => {
      cancelled = true;
    };
  }, [viewTemplate?.original_pdf_url]);

  useEffect(() => {
    const state = location.state as { highlightId?: string } | null;
    if (state?.highlightId) {
      setHighlightId(state.highlightId);
      // Clear the state so refreshing doesn't re-highlight
      window.history.replaceState({}, "");
      const timer = setTimeout(() => setHighlightId(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);
  // Open the uploader once for /forms/upload, then drop back to the canonical
  // list URL so a refresh doesn't reopen it. The modal itself lives in
  // FormsRouter, outside <Routes>, so this navigation can't unmount it.
  useEffect(() => {
    if (!autoOpenUpload || !onUploadClick) return;
    onUploadClick();
    navigate("/forms", { replace: true });
  }, [autoOpenUpload, onUploadClick, navigate]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<FormTemplate | null>(
    null
  );
  const audioPlaybackModalRef = useRef<HTMLDialogElement>(null) as React.RefObject<HTMLDialogElement>;
  const uploadModalRef = useRef<HTMLDialogElement>(null) as React.RefObject<HTMLDialogElement>;
  const casePickerModalRef = useRef<HTMLDialogElement>(null) as React.RefObject<HTMLDialogElement>;
  const filesPickerModalRef = useRef<HTMLDialogElement>(null) as React.RefObject<HTMLDialogElement>;
  const fillDropdownRef = useRef<HTMLDivElement>(null);
  const submissionListRef = useRef<FormSubmissionListHandle>(null);
  const [editingName, setEditingName] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const [descriptionValue, setDescriptionValue] = useState("");
  const [descriptionHeight, setDescriptionHeight] = useState<number | undefined>(undefined);
  const descriptionRef = useRef<HTMLParagraphElement>(null);

  // PDF preview state
  const [previewPage, setPreviewPage] = useState(1);
  const [previewNumPages, setPreviewNumPages] = useState(0);
  const [previewMinHeight, setPreviewMinHeight] = useState<number | undefined>(undefined);
  const [pageLoading, setPageLoading] = useState(false);
  const pdfContainerRef = useRef<HTMLDivElement>(null);

  // Snapshot current height before page change so it doesn't collapse
  const handlePageChange = useCallback((newPage: number) => {
    if (pdfContainerRef.current) {
      setPreviewMinHeight(pdfContainerRef.current.offsetHeight);
    }
    setPageLoading(true);
    setPreviewPage(newPage);
  }, []);

  // Clear minHeight once new page has rendered so it sizes naturally
  const handlePageRender = useCallback(() => {
    setPreviewMinHeight(undefined);
    setPageLoading(false);
  }, []);

  const handleDelete = (template: FormTemplate) => {
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!templateToDelete) return;

    try {
      await axios.delete(`/api/forms/templates/${templateToDelete.id}`);
      toast.success("Template deleted successfully");
      await onRefresh();
      if (viewTemplate?.id === templateToDelete.id) {
        navigate("/forms");
      }
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Failed to delete template");
    } finally {
      setShowDeleteModal(false);
      setTemplateToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setTemplateToDelete(null);
  };

  const closeFillDropdown = () => {
    const details = fillDropdownRef.current?.querySelector("details");
    if (details) details.removeAttribute("open");
  };

  const handleRecordAudio = () => {
    closeFillDropdown();
    audioPlaybackModalRef.current?.showModal();
  };

  const handleUploadAudio = () => {
    closeFillDropdown();
    uploadModalRef.current?.showModal();
  };

  const handleFillFromCase = () => {
    closeFillDropdown();
    casePickerModalRef.current?.showModal();
  };

  const handleFillFromFiles = () => {
    closeFillDropdown();
    filesPickerModalRef.current?.showModal();
  };

  // What happens around a fill is the same for every way of starting one: list
  // the new submission, then open it when it finishes.
  const sourceFillConfig = () => ({
    user,
    templateId: viewTemplate!.id,
    onSubmissionCreated: () => submissionListRef.current?.refetch(),
    onSuccess: (data: any) => {
      navigate(`/forms/${viewTemplate!.id}/submissions/${data.submissionId}`, { replace: true });
    },
    onError: (err: string) => {
      toast.error(err || "Failed to fill the form");
    },
  });

  const handleAudioFileChange = async (
    fileOrEvent: File | Blob | React.ChangeEvent<HTMLInputElement>,
    audioType?: string,
    _method?: string
  ) => {
    if (!viewTemplate) return;
    const blob = fileOrEvent as Blob;
    const file = new File([blob], `recording-${Date.now()}.wav`, {
      type: "audio/wav",
    });
    const fileConfigs = [
      {
        file,
        audioType: (audioType || "narrative") as "narrative" | "interaction",
        speakers: [],
        additionalContext: "",
      },
    ];
    await processFormFill(fileConfigs, {
      user,
      templateId: viewTemplate.id,
      onSubmissionCreated: () => submissionListRef.current?.refetch(),
      onSuccess: (data) => {
        navigate(
          `/forms/${viewTemplate.id}/submissions/${data.submissionId}`,
          { replace: true }
        );
      },
      onError: (err) => {
        toast.error(err || "Failed to process audio");
      },
    });
  };

  const handleUploadFiles = (
    files: {
      file: File;
      mediaType?: string;
      audioType: "narrative" | "interaction";
      speakers: { name: string; role: string }[];
      additionalContext: string;
      previousUpload?: {
        caseFileId: string;
        fileUrl: string;
        transcriptionUrl?: string;
        originalAudioType: "narrative" | "interaction";
        fileName: string;
        fileSize: string;
      };
      axonSource?: {
        evidenceId: string;
        title: string;
        size: number;
        duration: number;
        contentType: string;
      };
    }[],
    _options: { includeTimestamps: boolean; detailLevel: string }
  ) => {
    if (!viewTemplate) return;
    const fileConfigs = files.map(({ mediaType, ...rest }) => rest);
    processFormFill(fileConfigs, {
      user,
      templateId: viewTemplate.id,
      onSubmissionCreated: () => submissionListRef.current?.refetch(),
      onSuccess: (data) => {
        navigate(
          `/forms/${viewTemplate.id}/submissions/${data.submissionId}`,
          { replace: true }
        );
      },
      onError: (err) => {
        toast.error(err || "Failed to process audio");
      },
    });
  };

  const handleFillDropdownBlur = (e: React.FocusEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      closeFillDropdown();
    }
  };

  const isEditable = viewTemplate?.scope !== "agency";

  const handleDuplicate = async () => {
    if (!viewTemplate) return;
    try {
      const { data } = await axios.post("/api/forms/templates", {
        name: `${viewTemplate.name} (Copy)`,
        description: viewTemplate.description,
        original_pdf_url: viewTemplate.original_pdf_url,
        original_pdf_key: viewTemplate.original_pdf_key,
        is_fillable: viewTemplate.is_fillable,
        fields: viewTemplate.fields,
        field_sections: viewTemplate.field_sections,
        output_type: viewTemplate.output_type,
        text_output_template: viewTemplate.text_output_template,
        text_ai_style: viewTemplate.text_ai_style,
        scope: "personal",
      });
      toast.success("Template duplicated");
      await onRefresh();
      navigate("/forms", { state: { highlightId: data.id } });
    } catch {
      toast.error("Failed to duplicate template");
    }
  };

  const startEditingName = () => {
    if (!isEditable || !viewTemplate) return;
    setNameValue(viewTemplate.name);
    setEditingName(true);
  };

  const startEditingDescription = () => {
    if (!isEditable || !viewTemplate) return;
    if (descriptionRef.current) {
      setDescriptionHeight(descriptionRef.current.offsetHeight);
    }
    setDescriptionValue(viewTemplate.description || "");
    setEditingDescription(true);
  };

  const saveName = async () => {
    if (!viewTemplate) return;
    const trimmed = nameValue.trim();
    if (!trimmed || trimmed === viewTemplate.name) {
      setEditingName(false);
      return;
    }
    try {
      await axios.patch(`/api/forms/templates/${viewTemplate.id}`, { name: trimmed });
      await onRefresh();
      toast.success("Name updated");
    } catch {
      toast.error("Failed to update name");
    }
    setEditingName(false);
  };

  const saveDescription = async () => {
    if (!viewTemplate) return;
    const trimmed = descriptionValue.trim();
    if (trimmed === (viewTemplate.description || "")) {
      setEditingDescription(false);
      return;
    }
    try {
      await axios.patch(`/api/forms/templates/${viewTemplate.id}`, { description: trimmed });
      await onRefresh();
      toast.success("Description updated");
    } catch {
      toast.error("Failed to update description");
    }
    setEditingDescription(false);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex-1 overflow-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-base-300 rounded w-1/4" />
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-24 bg-base-300 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Template detail view
  if (viewTemplate) {
    const fieldCount = viewTemplate.fields?.length || 0;
    const sectionCount = viewTemplate.field_sections?.length || 0;
    const requiredCount = viewTemplate.fields?.filter((f) => f.required).length || 0;
    const fieldTypes = viewTemplate.fields?.reduce((acc, f) => {
      const type = f.type === "textarea" ? "text" : f.type;
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>) || {};

    return (
      <div className="flex-1 overflow-auto p-4 space-y-6">
        {/* Actions */}
        <div className="flex items-center justify-between">
          <Button variant="secondary" size="sm" onClick={() => navigate("/forms")}>
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Templates
          </Button>
          <div className="flex gap-2">
            <div
              ref={fillDropdownRef}
              className="dropdown dropdown-end"
              onBlur={handleFillDropdownBlur}
            >
              <Button tabIndex={0} variant="primary" size="sm">
                <div className="flex items-center">
                  <DocumentPlusIcon className="h-4 w-4 mr-1" />
                  Fill Form
                </div>
              </Button>
              <ul
                tabIndex={0}
                className="dropdown-content menu bg-base-100 rounded-lg z-[1] w-52 p-2 shadow mt-2"
              >
                <li>
                  <button onClick={handleRecordAudio} className="hover:bg-gray-100 dark:hover:bg-base-200">
                    <MicrophoneIcon className="size-4" />
                    Record Audio
                  </button>
                </li>
                <li>
                  <button onClick={handleUploadAudio} className="hover:bg-gray-100 dark:hover:bg-base-200">
                    <ArrowUpTrayIcon className="size-4" />
                    Upload Audio
                  </button>
                </li>
                <li>
                  <button onClick={handleFillFromCase} className="hover:bg-gray-100 dark:hover:bg-base-200">
                    <DocumentTextIcon className="size-4" />
                    From a Case
                  </button>
                </li>
                <li>
                  <button onClick={handleFillFromFiles} className="hover:bg-gray-100 dark:hover:bg-base-200">
                    <FolderIcon className="size-4" />
                    From Files
                  </button>
                </li>
              </ul>
            </div>
            {viewTemplate.scope === "agency" ? (
              <Tooltip overlay="Create a personal copy of this template that you can edit" placement="bottomLeft">
                <Button variant="secondary" size="sm" onClick={handleDuplicate}>
                  <DocumentDuplicateIcon className="h-4 w-4 mr-1" />
                  Duplicate
                </Button>
              </Tooltip>
            ) : (
              <Button variant="secondary" size="sm" onClick={handleDuplicate}>
                <DocumentDuplicateIcon className="h-4 w-4 mr-1" />
                Duplicate
              </Button>
            )}
            {viewTemplate.scope !== "agency" && (
              <>
                <Button variant="secondary" size="sm" onClick={() => navigate(`/forms/${viewTemplate.id}/edit`)} data-tour="form-edit-fields">
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Edit Fields
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDelete(viewTemplate)}
                >
                  <TrashIcon className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Title & Description */}
        <div>
          {editingName ? (
            <input
              autoFocus
              className="text-2xl font-semibold bg-transparent border-none outline-none w-full max-w-xl text-blue-600 dark:text-blue-300 caret-primary p-0 m-0 leading-[inherit]"
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveName();
                if (e.key === "Escape") setEditingName(false);
              }}
            />
          ) : (
            <h1
              className={`text-2xl font-semibold ${isEditable ? "cursor-pointer hover:text-blue-500 dark:hover:text-blue-300 transition-colors" : ""}`}
              onClick={startEditingName}
            >
              {viewTemplate.name}
            </h1>
          )}
          {editingDescription ? (
            <textarea
              autoFocus
              className="text-base-content/60 mt-1 text-sm leading-normal max-w-xl w-full bg-transparent border-none outline-none resize-none text-blue-700 dark:text-blue-300 caret-primary p-0 m-0 block"
              style={{ height: descriptionHeight }}
              value={descriptionValue}
              onChange={(e) => {
                setDescriptionValue(e.target.value);
                e.target.style.height = "auto";
                e.target.style.height = e.target.scrollHeight + "px";
              }}
              onBlur={saveDescription}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveDescription(); }
                if (e.key === "Escape") setEditingDescription(false);
              }}
              placeholder="Add a description..."
            />
          ) : (
            <p
              ref={descriptionRef}
              className={`text-base-content/60 mt-1 text-sm leading-normal max-w-xl ${isEditable ? "cursor-pointer hover:text-blue-600 dark:hover:text-blue-300 transition-colors" : ""}`}
              onClick={startEditingDescription}
            >
              {viewTemplate.description || (isEditable ? "Add a description..." : "")}
            </p>
          )}
          <div className="flex items-center gap-3 mt-2">
            <span className="inline-flex items-center gap-1 text-xs text-base-content/50">
              {viewTemplate.scope === "agency" ? (
                <BuildingOfficeIcon className="h-3.5 w-3.5" />
              ) : (
                <UserIcon className="h-3.5 w-3.5" />
              )}
              {viewTemplate.scope === "agency" ? "Agency" : "Personal"}
            </span>
            <span
              className={`text-xs px-2 py-0.5 rounded-full ${
                viewTemplate.is_fillable ? "bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-400" : "bg-yellow-100 text-yellow-800 dark:bg-amber-950/40 dark:text-amber-400"
              }`}
            >
              {viewTemplate.is_fillable ? "Fillable PDF" : "Flat PDF"}
            </span>
          </div>
        </div>

        {/* Two-column: PDF preview + stats */}
        <div className="relative flex gap-6 max-h-[400px]">
          {/* PDF Preview — sets the row height */}
          <div className="w-1/2 flex-shrink-0 overflow-hidden">
            <div
              ref={pdfContainerRef}
              className="bg-gray-100 dark:bg-base-200 rounded-xl border border-base-300 overflow-hidden flex flex-col h-full"
              style={{ minHeight: previewMinHeight }}
            >
              {/* paper — stays white in dark mode */}
              <div className="bg-white border-b border-base-300 flex-1 relative overflow-y-auto">
                {pageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center z-10">
                    <span className="loading loading-spinner loading-md text-base-content/30" />
                  </div>
                )}
                {!previewPdfUrl ? (
                  <div className="flex items-center justify-center" style={{ height: 400 }}>
                    <span className="loading loading-spinner loading-md text-base-content/30" />
                  </div>
                ) : (
                <Document
                  file={previewPdfUrl}
                  onLoadSuccess={({ numPages }) => {
                    setPreviewNumPages(numPages);
                    setPreviewPage(1);
                  }}
                  loading={
                    <div className="flex items-center justify-center" style={{ height: 400 }}>
                      <span className="loading loading-spinner loading-md text-base-content/30" />
                    </div>
                  }
                  error={
                    <div className="flex items-center justify-center text-base-content/40 text-sm" style={{ height: 400 }}>
                      Failed to load PDF
                    </div>
                  }
                >
                  <div
                    className="transition-opacity duration-200"
                    style={{ opacity: pageLoading ? 0 : 1 }}
                  >
                    <Page
                      pageNumber={previewPage}
                      width={undefined}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      className="[&_canvas]:!w-full [&_canvas]:!h-auto"
                      onRenderSuccess={handlePageRender}
                      loading={<span />}
                    />
                  </div>
                </Document>
                )}
              </div>
              {previewNumPages > 1 && (
                <div className="flex items-center justify-center gap-3 py-2 bg-base-200">
                  <button
                    onClick={() => handlePageChange(Math.max(1, previewPage - 1))}
                    disabled={previewPage <= 1}
                    className="p-1 rounded hover:bg-base-300 disabled:opacity-30 transition-colors"
                  >
                    <ChevronLeftIcon className="h-4 w-4 text-gray-600 dark:text-base-content/70" />
                  </button>
                  <span className="text-xs text-base-content/60 font-medium min-w-[5rem] text-center">
                    Page {previewPage} of {previewNumPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(Math.min(previewNumPages, previewPage + 1))}
                    disabled={previewPage >= previewNumPages}
                    className="p-1 rounded hover:bg-base-300 disabled:opacity-30 transition-colors"
                  >
                    <ChevronRightIcon className="h-4 w-4 text-gray-600 dark:text-base-content/70" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Stats & Info — constrained to PDF column height */}
          <div className="absolute right-0 top-0 bottom-0 left-[calc(50%+0.75rem)] overflow-y-auto space-y-4">
            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-3" data-tour="form-template-stats">
              <div className="bg-blue-50 dark:bg-blue-900/40 rounded-lg p-3">
                <div className="text-2xl font-semibold text-blue-700 dark:text-blue-300">{fieldCount}</div>
                <div className="text-xs text-blue-600/70 dark:text-blue-300/70 mt-0.5">Fields</div>
              </div>
              <div className="bg-purple-50 dark:bg-purple-950/40 rounded-lg p-3">
                <div className="text-2xl font-semibold text-purple-700 dark:text-purple-400">{sectionCount}</div>
                <div className="text-xs text-purple-600/70 dark:text-purple-400/70 mt-0.5">Sections</div>
              </div>
              <div className="bg-amber-50 dark:bg-amber-950/40 rounded-lg p-3">
                <div className="text-2xl font-semibold text-amber-700 dark:text-amber-400">{requiredCount}</div>
                <div className="text-xs text-amber-600/70 dark:text-amber-400/70 mt-0.5">Required</div>
              </div>
            </div>

            {/* Field types breakdown */}
            {fieldCount > 0 && (
              <div className="bg-base-100 rounded-lg border border-base-300 p-4">
                <h3 className="text-sm font-medium text-base-content/70 mb-3">Field Types</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(fieldTypes)
                    .sort(([, a], [, b]) => b - a)
                    .map(([type, count]) => (
                      <span
                        key={type}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 dark:bg-base-200 rounded-md text-xs text-gray-600 dark:text-base-content/70"
                      >
                        <span className="font-medium capitalize">{type}</span>
                        <span className="text-base-content/40">{count}</span>
                      </span>
                    ))}
                </div>
              </div>
            )}

            {/* Sections list */}
            {sectionCount > 0 && (
              <div className="bg-base-100 rounded-lg border border-base-300 p-4">
                <h3 className="text-sm font-medium text-base-content/70 mb-3">Sections</h3>
                <div className="space-y-1.5">
                  {viewTemplate.field_sections!.map((sec, i) => {
                    const sectionFieldCount = viewTemplate.fields?.filter(
                      (f) => f.section === sec.name
                    ).length || 0;
                    return (
                      <div
                        key={i}
                        className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-base-200"
                      >
                        <span className="text-sm text-base-content/80">{sec.name}</span>
                        <span className="text-xs text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/40 px-1.5 py-0.5 rounded whitespace-nowrap flex-shrink-0">
                          {sectionFieldCount} {sectionFieldCount === 1 ? "field" : "fields"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Submissions section */}
        <FormSubmissionList
          ref={submissionListRef}
          template={viewTemplate}
          onRecordAudio={handleRecordAudio}
          onUploadAudio={handleUploadAudio}
          onFillFromCase={handleFillFromCase}
          onFillFromFiles={handleFillFromFiles}
          onSelect={(submission) => {
            navigate(`/forms/${viewTemplate.id}/submissions/${submission.id}`);
          }}
        />

        {/* Delete confirmation modal */}
        {showDeleteModal && (
          <dialog className="modal modal-open">
            <div className="modal-box">
              <h3 className="font-bold text-lg">Delete Template</h3>
              <p className="py-4">
                Are you sure you want to delete "{templateToDelete?.name}"? This
                action cannot be undone.
              </p>
              <div className="modal-action">
                <Button variant="secondary" onClick={cancelDelete}>
                  Cancel
                </Button>
                <Button variant="danger" onClick={confirmDelete}>
                  Delete
                </Button>
              </div>
            </div>
            <form method="dialog" className="modal-backdrop">
              <button onClick={cancelDelete}>close</button>
            </form>
          </dialog>
        )}

        {/* Audio recording modal — shows the form PDF alongside for reference */}
        <AudioPlayback
          modalRef={audioPlaybackModalRef}
          handleAudioFileChange={handleAudioFileChange}
          pdfUrl={previewPdfUrl || undefined}
        />

        {/* Upload audio modal */}
        <UploadModal
          modalRef={uploadModalRef}
          onUpload={handleUploadFiles}
          hideReportTemplate
          hasEvidenceIntegration={hasEvidenceIntegration}
        />

        {/* Fill from a case, or from chosen Files */}
        <CaseFillPickerModal
          modalRef={casePickerModalRef}
          onFill={(caseId) => void fillFormFromCase(caseId, sourceFillConfig())}
        />
        <FilesFillPickerModal
          modalRef={filesPickerModalRef}
          onFill={(files) => void fillFormFromFiles(files.map((f) => ({ source: f.source, id: f.id })), sourceFillConfig())}
        />
      </div>
    );
  }

  // Template list view
  const columns: ListColumn<FormTemplate, TemplateSortKey>[] = [
    {
      key: "name",
      header: "Name",
      sortKey: "name",
      render: (template) => (
        <div className="flex items-center gap-2 min-w-0">
          <DocumentIcon className="h-5 w-5 text-primary flex-shrink-0" />
          <div className="min-w-0">
            <p className="font-medium truncate" data-tour="form-template-name">{template.name}</p>
            {isTemplateProcessing(template) ? (
              <p className="text-xs text-blue-600 dark:text-blue-300 flex items-center gap-1">
                <span className="loading loading-spinner size-3" /> Processing: reading the form and placing fields...
              </p>
            ) : hasPendingSuggestions(template) ? (
              <p className="text-xs text-amber-700 dark:text-amber-400 truncate">AI suggestions ready to review</p>
            ) : (
              template.description && <p className="text-xs text-base-content/40 truncate max-w-md">{template.description}</p>
            )}
          </div>
        </div>
      ),
    },
    { key: "fields", header: "Fields", sortKey: "fields", defaultDir: "desc", className: "text-sm text-base-content/60", render: (t) => t.fields?.length || 0 },
    {
      key: "type",
      header: "Type",
      sortKey: "type",
      render: (t) =>
        isTemplateProcessing(t) ? (
          <span className="text-xs px-2 py-0.5 rounded-full whitespace-nowrap bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
            Processing
          </span>
        ) : (
        <span
          className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
            t.is_fillable
              ? "bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-400"
              : "bg-yellow-100 text-yellow-800 dark:bg-amber-950/40 dark:text-amber-400"
          }`}
        >
          {t.is_fillable ? "Fillable" : "Flat"}
        </span>
        ),
    },
    {
      key: "scope",
      header: "Scope",
      sortKey: "scope",
      render: (t) => (
        <span className="inline-flex items-center gap-1 text-xs text-base-content/60 whitespace-nowrap">
          {t.scope === "agency" ? <BuildingOfficeIcon className="h-3.5 w-3.5" /> : <UserIcon className="h-3.5 w-3.5" />}
          {t.scope === "agency" ? "Dept" : "Personal"}
        </span>
      ),
    },
    {
      key: "created_at",
      header: "Created",
      sortKey: "created_at",
      defaultDir: "desc",
      className: "text-sm text-base-content/60 whitespace-nowrap",
      render: (t) => (t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"),
    },
    {
      key: "updated_at",
      header: "Modified",
      sortKey: "updated_at",
      defaultDir: "desc",
      className: "text-sm text-base-content/60 whitespace-nowrap",
      render: (t) => (t.updated_at ? new Date(t.updated_at).toLocaleDateString() : "—"),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (template) =>
        template.scope !== "agency" &&
        !isTemplateProcessing(template) && (
          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-base-200 text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70 transition-colors"
              onClick={() => navigate(`/forms/${template.id}/edit`)}
              title="Edit"
            >
              <PencilIcon className="h-3.5 w-3.5" />
            </button>
            <button
              className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-950/40 text-base-content/40 hover:text-red-500 transition-colors"
              onClick={() => handleDelete(template)}
              title="Delete"
            >
              <TrashIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        ),
    },
  ];

  return (
    <ListPage<FormTemplate, TemplateSortKey>
      onFilesDropped={onFilesDropped}
      dropMessage="Drop a PDF to create a form template"
      title="Form Templates"
      subtitle="Upload PDF forms and configure fields for AI-powered form filling"
      action={
        <Button variant="primary" onClick={onUploadClick} data-tour="forms-upload-button">
          <PlusIcon className="h-5 w-5 mr-1" />
          Upload Template
        </Button>
      }
      items={sortedTemplates}
      getKey={(t) => t.id}
      columns={columns}
      sort={sort}
      onSort={setSort}
      onRowClick={(t) => navigate(hasPendingSuggestions(t) ? `/forms/${t.id}/edit` : `/forms/${t.id}`)}
      rowClassName={(t) =>
        isTemplateProcessing(t)
          ? "opacity-50 grayscale pointer-events-none select-none"
          : t.id === highlightId
            ? "animate-highlight-fade"
            : undefined
      }
      search={{ value: search, onChange: setSearch, placeholder: "Search templates..." }}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      renderCard={(template) => (
        <TemplateCard template={template} onNavigate={(path) => navigate(path)} onDelete={handleDelete} highlighted={template.id === highlightId} />
      )}
      cardGroups={(list) => [
        {
          key: "agency",
          title: "Department Templates",
          icon: <BuildingOfficeIcon className="h-4 w-4 text-base-content/40" />,
          items: list.filter((t) => t.scope === "agency"),
        },
        {
          key: "personal",
          title: "My Templates",
          icon: <UserIcon className="h-4 w-4 text-base-content/40" />,
          items: list.filter((t) => t.scope !== "agency"),
        },
      ]}
      isFiltered={Boolean(search.trim())}
      noMatch={`No templates match "${search.trim()}"`}
      empty={{
        icon: <DocumentArrowUpIcon className="h-12 w-12" />,
        title: "No form templates yet",
        description: "Upload a PDF form to get started. The system will automatically detect form fields or let you place them manually.",
        action: (
          <Button variant="primary" onClick={onUploadClick}>
            <PlusIcon className="h-5 w-5 mr-1" />
            Upload Your First Template
          </Button>
        ),
      }}
    >
      {/* The second half of the main Forms page: everything filled out so far,
          across all forms, under the forms they can fill out. */}
      <section className="pt-4 border-t border-base-300">
        <FormSubmissionList onSelect={(s) => navigate(`/forms/${s.template_id}/submissions/${s.id}`)} />
      </section>

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <dialog className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Delete Template</h3>
            <p className="py-4">
              Are you sure you want to delete "{templateToDelete?.name}"? This
              action cannot be undone.
            </p>
            <div className="modal-action">
              <Button variant="secondary" onClick={cancelDelete}>
                Cancel
              </Button>
              <Button variant="danger" onClick={confirmDelete}>
                Delete
              </Button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button onClick={cancelDelete}>close</button>
          </form>
        </dialog>
      )}
    </ListPage>
  );
};

export default FormTemplateManager;
